import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import { computeAttendanceAnalytics } from "../services/attendanceAnalyticsService";

function Card({ label, value, color = "text.primary" }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, textAlign: "center", height: "100%" }}>
      <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={700}>{label}</Typography>
      <Typography variant="h4" fontWeight={800} sx={{ color }}>{value}</Typography>
    </Paper>
  );
}

/**
 * Attendance dashboard + anomaly explorer for the "Wins Attendance" collection.
 * Reuses computeAttendanceAnalytics over the currently displayed rows.
 */
export default function AttendanceAnalyticsDialog({ open, onClose, rows, onHighlight, onOpenEmployee }) {
  const [tab, setTab] = useState(0);
  const analytics = useMemo(() => (open ? computeAttendanceAnalytics(rows || []) : null), [open, rows]);

  if (!analytics) return null;

  const highlight = (ids) => onHighlight?.(ids);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Attendance Analytics</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid size={{ xs: 6, sm: 3 }}><Card label="Today's Punches" value={analytics.todayPunches} /></Grid>
          <Grid size={{ xs: 6, sm: 3 }}><Card label="Present" value={analytics.present} color="success.main" /></Grid>
          <Grid size={{ xs: 6, sm: 3 }}><Card label="IN" value={analytics.in} color="success.main" /></Grid>
          <Grid size={{ xs: 6, sm: 3 }}><Card label="OUT" value={analytics.out} color="info.main" /></Grid>
          <Grid size={{ xs: 6, sm: 3 }}><Card label="Late" value={analytics.late} color="warning.main" /></Grid>
          <Grid size={{ xs: 6, sm: 3 }}><Card label="Duplicate" value={analytics.duplicate} color="error.main" /></Grid>
          <Grid size={{ xs: 6, sm: 3 }}><Card label="Missing OUT" value={analytics.missingOut} color="error.main" /></Grid>
          <Grid size={{ xs: 6, sm: 3 }}><Card label="Missing IN" value={analytics.missingIn} color="error.main" /></Grid>
        </Grid>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <Chip size="small" label={`Employees: ${analytics.employees}`} />
          <Chip size="small" color="primary" variant="outlined" label={`Branches: ${analytics.branches.length}`} />
          <Chip size="small" color="secondary" variant="outlined" label={`Machines: ${analytics.machines.length}`} />
        </Stack>

        <Divider sx={{ my: 1 }} />

        <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label={`Duplicates (${analytics.duplicateGroups.length})`} />
          <Tab label={`Missing OUT (${analytics.missingOutRecords.length})`} />
          <Tab label={`Missing IN (${analytics.missingInRecords.length})`} />
          <Tab label="Branches & Machines" />
        </Tabs>

        <Box sx={{ mt: 2 }}>
          {tab === 0 && (
            <>
              <Button size="small" sx={{ mb: 1 }} onClick={() => highlight(analytics.duplicateIds)} disabled={!analytics.duplicateIds.size}>
                Highlight duplicates in grid
              </Button>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell><TableCell>Device</TableCell><TableCell>Time</TableCell><TableCell align="right">Count</TableCell><TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.duplicateGroups.map((g) => (
                    <TableRow key={g.key} hover>
                      <TableCell>{g.employeeId}</TableCell>
                      <TableCell>{g.device || "—"}</TableCell>
                      <TableCell>{g.time}</TableCell>
                      <TableCell align="right"><Chip size="small" color="error" label={g.count} /></TableCell>
                      <TableCell align="right"><Button size="small" onClick={() => onOpenEmployee?.(g.employeeId)}>Timeline</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          {tab === 1 && (
            <AnomalyTable records={analytics.missingOutRecords} ids={analytics.missingOutIds} onHighlight={highlight} onOpenEmployee={onOpenEmployee} />
          )}
          {tab === 2 && (
            <AnomalyTable records={analytics.missingInRecords} ids={analytics.missingInIds} onHighlight={highlight} onOpenEmployee={onOpenEmployee} />
          )}
          {tab === 3 && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" gutterBottom>Branches</Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {analytics.branches.length ? analytics.branches.map((b) => <Chip key={b} size="small" label={b} />) : <Typography color="text.secondary">—</Typography>}
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" gutterBottom>Machines</Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {analytics.machines.length ? analytics.machines.map((m) => <Chip key={m} size="small" label={m} />) : <Typography color="text.secondary">—</Typography>}
                </Stack>
              </Grid>
            </Grid>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={() => highlight(new Set())}>Clear highlight</Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function AnomalyTable({ records, ids, onHighlight, onOpenEmployee }) {
  return (
    <>
      <Button size="small" sx={{ mb: 1 }} onClick={() => onHighlight(ids)} disabled={!ids.size}>
        Highlight in grid
      </Button>
      {records.length === 0 ? (
        <Typography color="text.secondary">None detected.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell><TableCell>Time</TableCell><TableCell>Issue</TableCell><TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.employeeId}</TableCell>
                <TableCell>{r.time}</TableCell>
                <TableCell>{r.issue}</TableCell>
                <TableCell align="right"><Button size="small" onClick={() => onOpenEmployee?.(r.employeeId)}>Timeline</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}
